import React from 'react'
import { Button, EyeIcon, Flex, H2, Line, SelectDropdown, Span } from '../../lib/components'
import { Pagination } from '../../lib/components/Pagination'
import { InvestmentLimitRequestListItem } from '../../lib/types'
import { displayDollar, getTimestampWithTimezone } from '../../lib/utils/helpers'
import './investment-limit-requests.scss'

export const INVESTMENT_LIMIT_REQUEST_STATUS_FILTERS = [
  { text: 'Submitted', value: 'SUBMITTED' },
  { text: 'Approved', value: 'APPROVED' },
  { text: 'Rejected', value: 'REJECTED' },
  { text: 'All', value: 'ALL' },
]

export type InvestmentLimitRequestsListPresenterProps = {
  requests: InvestmentLimitRequestListItem[]
  isLoading: boolean
  status: string
  investorIdFilter?: string
  handleStatusChange: (value: string) => void
  resetFilters: () => void
  pagination: { total: number; limit: number; count: number; offset: number }
  currentPage: number
  handleCurrentPage: (page: number) => void
  handleView: (row: InvestmentLimitRequestListItem) => void
}

const formatDate = (value?: string) => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return getTimestampWithTimezone(date, 'DD MMM YYYY, HH:mm z')
}

export function InvestmentLimitRequestsListPresenter({
  requests,
  isLoading,
  status,
  investorIdFilter,
  handleStatusChange,
  resetFilters,
  pagination,
  currentPage,
  handleCurrentPage,
  handleView,
}: InvestmentLimitRequestsListPresenterProps) {
  return (
    <Flex variant='column-center'>
      <div className='investment-limit-requests w-100'>
        <H2 classes={['mb-48']} text='Investment Limit Requests' />
        <div className='toolbar-layout gap-12 mb-16 mt-16'>
          <SelectDropdown
            name='status'
            id='investment-limit-request-status'
            formId=''
            list={INVESTMENT_LIMIT_REQUEST_STATUS_FILTERS}
            value={status}
            placeholder='Filter by Status'
            handleChange={handleStatusChange}
            dataTestId='investment-limit-requests-status-filter'
          />
          <div className='sort-reset'>
            <Button
              handleClick={resetFilters}
              classes={['reset-btn']}
              variant='secondary-btn'
              text='Reset'
              dataTestId='investment-limit-requests-reset'
            />
          </div>
        </div>
        {investorIdFilter ? (
          <Span
            classes={['mb-16']}
            variant='thin'
            text={`Filtered by investor ID: ${investorIdFilter}`}
            dataTestId='investment-limit-requests-investor-filter'
          />
        ) : null}
        <Line />
        {isLoading ? (
          <p
            className='investment-limit-requests__loading'
            data-testid='investment-limit-requests-loading'
          >
            Loading…
          </p>
        ) : requests.length === 0 ? (
          <Span
            classes={['center', 'mt-24']}
            variant='thin'
            text='No Investment Limit Requests Found'
            dataTestId='investment-limit-requests-empty'
          />
        ) : (
          <div className='w-100'>
            <Flex
              variant='row'
              classes={['mb-24', 'table_scroll_horizontal']}
              dataTestId='investment-limit-requests-table-container'
            >
              <table className='styled-table'>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Client ID</th>
                    <th>Current Limit</th>
                    <th>Requested Range</th>
                    <th>Status</th>
                    <th>Docs</th>
                    <th>Submitted</th>
                    <th>View</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((row) => (
                    <tr key={row.id} data-testid={`investment-limit-request-row-${row.id}`}>
                      <td>{row.investorName || '—'}</td>
                      <td>{row.investorEmail || '—'}</td>
                      <td>{row.clientId || '—'}</td>
                      <td data-testid={`investment-limit-request-current-${row.id}`}>
                        {displayDollar(String(row.currentInvestmentLimit ?? ''), 'USD', 2, true)}
                      </td>
                      <td>{row.requestedLimitRangeLabel || row.requestedLimitRange || '—'}</td>
                      <td>{row.status || '—'}</td>
                      <td>{row.documentCount ?? 0}</td>
                      <td>{formatDate(row.createdAt)}</td>
                      <td>
                        <EyeIcon
                          classes={['align-center', 'pointer']}
                          height='24px'
                          width='24px'
                          onClick={() => handleView(row)}
                          dataTestId={`investment-limit-request-view-${row.id}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Flex>
            {pagination.total > pagination.limit && (
              <Pagination
                pagination={pagination}
                handleCurrentPage={handleCurrentPage}
                currentPage={currentPage}
              />
            )}
          </div>
        )}
      </div>
    </Flex>
  )
}
